import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useFocus, writeFocusHeartbeat, type FocusEndReason } from '@/src/renderer/lib/focus'
import type { Task } from '@/src/renderer/lib/db/models'
import { isQuickNote } from '@/src/renderer/lib/db/taskFilters'
import type { FocusCardProps } from '../components/FocusCard'
import { useFocusClock } from './useFocusClock'

/** Wie oft die laufende Fokus-Fenster ein Lebenszeichen hinterlässt. */
export const HEARTBEAT_MS = 30_000

export interface FocusSession {
  /**
   * Die Aufgabe, auf der der Fokus läuft — `null`, wenn keiner läuft (oder die
   * Datenquelle noch lädt). Die Hüllen brauchen sie, weil das Erledigen bei
   * ihnen liegt (siehe {@link FocusSession.complete}).
   */
  task: Task | null
  /**
   * Alles, was die {@link FocusCard} braucht — **außer dem Haken**. `null` heißt:
   * nichts zu zeichnen.
   */
  card: Omit<FocusCardProps, 'onComplete'> | null
  /** Den laufenden Abschnitt buchen und den Fokus beenden. */
  end: (options?: { at?: Date; reason?: FocusEndReason }) => Promise<void>
  /**
   * Der Haken, in der richtigen Reihenfolge: erst erledigen, dann buchen.
   *
   * Das Erledigen selbst gehört `taskManagement` — und ein Feature importiert
   * kein anderes Feature (CLAUDE.md). Die Hülle reicht darum ihr
   * `handleToggleComplete` herein; hier steht nur die Reihenfolge, damit sie
   * nicht in zwei Hüllen getrennt altert.
   */
  complete: (toggleComplete: (ids: Set<string>) => Promise<void> | void) => Promise<void>
}

/**
 * **Der laufende Fokus, fertig zum Anzeigen** — der geteilte Kern hinter beiden
 * Hüllen des Fokus-Fensters: dem Fokus-Fenster auf dem Desktop (`windows/FocusWindow`)
 * und dem Überzug im Web (`FocusOverlayHost`).
 *
 * Er hält **keinen** Zustand. Was er zeigt, liest er über `useDataSource()` aus
 * derselben Datenbank, in die `useFocus` schreibt (ADR 0025) — darum sehen im
 * Electron-Fall beide Fenster dieselbe Uhr, ohne einander etwas zu schicken.
 *
 * **Was hier NICHT steht**, weil es zur jeweiligen Hülle gehört:
 * - der durchsichtige Grund und der IPC-Weg `focus:end-requested` (nur Fenster),
 * - der Schleier und das Abfangen von Escape (nur Web),
 * - das Erledigen selbst (beide, siehe {@link FocusSession.complete}).
 *
 * @example
 * const { task, card, complete } = useFocusSession()
 * const { handleToggleComplete } = useTaskOperations({ t, tasks: task ? [task] : [], momentOrigin: 'focus' })
 * if (!card) return null
 * return <FocusCard {...card} onComplete={() => void complete(handleToggleComplete)} />
 */
export function useFocusSession(): FocusSession {
  const source = useDataSource()
  const { t } = useTranslation()
  const { endFocus } = useFocus()

  const entry = source.useRunningFocus()
  const task = source.useTask(entry?.taskId)
  const subtasks = source.useSubtasks(entry?.taskId)
  /*
    Als eigene Konstante und nicht inline: Ein frisches Array je Render ließe die
    Live-Query bei jedem Durchlauf neu abonnieren.
  */
  const focusIds = useMemo(() => (entry ? [entry.taskId] : []), [entry])
  const bookedByTask = source.useFocusSeconds(focusIds)

  /*
    **Die erste Zeile der Karte** (Figma 5420-53414): das Projekt. Eine Notiz
    aus **Kurz notiert** hat keins — `projectId` ist dort `null`, solange sie
    unsortiert liegt. Dann trägt der Name dieser Seite die Zeile; das Design
    zeigt genau diesen Fall („Kurz notiert" über „Aufgaben sammeln").
  */
  const project = source.useProject(task?.projectId ?? undefined)
  /*
    Eine **Unteraufgabe** (T14 #179) trägt dort ihre Aufgabe: „Umzug" über
    „Nachsendeauftrag" sagt mehr als das Projekt — und ohne Projekt stünde sonst nichts da.
    Bei einer tiefen die Aufgabe ganz oben (#181): Dort findet man sie.
  */
  const chain = source.useTaskAncestors(task?.parentTaskId ? task.id : undefined)
  const top = chain?.[chain.length - 1]
  const projectName = top?.title || project?.name || (task && isQuickNote(task) ? t('quickNotes.title') : null)

  const targetSeconds = task?.estimatedDuration != null ? task.estimatedDuration * 60 : null
  const clock = useFocusClock({
    startTime: entry?.startTime ?? null,
    bookedSeconds: entry ? bookedByTask?.get(entry.taskId) ?? 0 : 0,
    targetSeconds,
  })

  /*
    **Das Lebenszeichen.** Gerätelokal und nicht synchronisiert: Es sagt nur
    diesem Rechner, bis wann das Fokus-Fenster zuletzt lief. Stürzt die App ab, wird es
    hart beendet oder der Tab geschlossen, bleibt ein laufender Abschnitt ohne
    Ende zurück — beim nächsten Start wird er bis hierher gebucht
    (`useFocusRecovery`, `FOCUS_GRACE_MS`).

    Er steht seit 3d hier und nicht mehr in `FocusWindow`: Im Web gibt es kein
    Fokus-Fenster, und zwei Schreiber für dieselbe Zahl wären zwei Stellen, an
    denen sie ausbleiben kann.
  */
  useEffect(() => {
    if (!entry) return
    writeFocusHeartbeat()
    const id = setInterval(writeFocusHeartbeat, HEARTBEAT_MS)
    return () => clearInterval(id)
  }, [entry])

  const complete = useCallback(async (toggleComplete: (ids: Set<string>) => Promise<void> | void) => {
    if (!task) return
    // Erst erledigen, dann beenden: Die gebuchte Zeit gehört noch zu dieser
    // Aufgabe, und der Geschafft-Moment (3c) hängt am Haken.
    await toggleComplete(new Set([task.id]))
    await endFocus({ reason: 'completed' })
  }, [task, endFocus])

  const open = subtasks?.filter(s => !s.deletedAt) ?? []

  return {
    task: task ?? null,
    card: entry && task
      ? {
        title: task.title,
        projectName,
        lifeSpace: task.lifeSpace,
        seconds: clock.seconds,
        targetSeconds,
        rewardId: task.rewardId,
        subtasks: open.length > 0 ? { done: open.filter(s => s.done).length, total: open.length } : null,
        onPause: () => void endFocus({ reason: 'pause' }),
      }
      : null,
    end: endFocus,
    complete,
  }
}
